<template>
  <div v-if="picAtivo && (loading || offers.length || error)" class="pic-oferta-inline mt-50">
    <b-spinner v-if="loading" small class="mr-50" />
    <span v-if="loading" class="text-muted">Consultando ofertas PIC...</span>
    <b-alert v-if="error" show variant="warning" class="py-50 px-75 mb-0">
      {{ error }}
    </b-alert>
    <div v-for="offer in offers" :key="`${offer.picid}-${offer.itemid}`"
      class="d-flex align-items-center justify-content-between border rounded px-75 py-50 mt-50">
      <div>
        <strong class="text-primary">Oferta PIC</strong>
        <span class="ml-50">{{ offer.descricao || `PIC ${offer.picid}` }}</span>
        <span class="ml-50 text-success">
          <small v-if="valorDeCustoVisual">Valor de custo:</small>
          {{ precoOfertaVisual(offer) === null ? '—' : formatPrice(precoOfertaVisual(offer)) }}
        </span>
        <span v-if="!precoEspecialVisual" class="ml-50 text-muted text-decoration-line-through">
          {{ formatPrice(offer.precoBase) }}
        </span>
        <b-badge v-if="!precoEspecialVisual && offer.desconto != null" variant="light-danger" class="ml-50">
          -{{ Number(offer.desconto).toFixed(2) }}%
        </b-badge>
        <small v-if="offer.faixas.length" class="d-block text-muted">
          Faixas: {{ formatFaixas(offer.faixas) }}
        </small>
        <small v-if="offer.regraConsumo === 'QE'" class="d-block text-info">
          QE exige a promoção completa; não é possível usar somente este SKU.
        </small>
      </div>
      <!-- Deliberately a button, not a checked checkbox: discovering an offer
           must never apply it to the order implicitly. -->
      <b-button size="sm" variant="outline-primary"
        :disabled="loading || offer.regraConsumo === 'QE'"
        @click="$emit('use-pic', offer)">
        Usar PIC
      </b-button>
    </div>
  </div>
</template>

<script>
import { BAlert, BBadge, BButton, BSpinner } from 'bootstrap-vue';
import SLSPicService from '@/services/Operacoes/SLSPic.service';
import store from '@/store';
import { precoVisualizacao, ehValorDeCusto } from '@/utils/precoVisualizacao';

const picService = new SLSPicService();

export default {
  name: 'PicOfertaInline',
  components: {
    BAlert,
    BBadge,
    BButton,
    BSpinner,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    context: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      offers: [],
      error: '',
      requestId: 0,
    };
  },
  computed: {
    tipoPedidoVisual() {
      return this.context.tipoPedidoCliente?.tipopedidoid
        || this.context.tipoPedido?.tipopedidoid || this.context.dataCapaPedido?.tipopedidoid;
    },
    valorDeCustoVisual() {
      return ehValorDeCusto(this.tipoPedidoVisual);
    },
    precoEspecialVisual() {
      return ['WS', 'WC', 'WD'].includes(String(this.tipoPedidoVisual || '').trim().toUpperCase())
        || this.item.assessmentFlag === true;
    },
    picAtivo() {
      return store.getters['preferencias/picAtivo'] === true;
    },
    itemId() {
      return this.item.itemIdUnico
        || this.item.item_id
        || this.item.itemId
        || this.item.sku
        || this.item.itemid;
    },
    contextKey() {
      return JSON.stringify({
        item: this.itemId,
        picAtivo: this.picAtivo,
        clienteid: this.context.cliente?.clienteid,
        estabelecimentoid: this.context.estabelecimento?.estabelecimentoid,
        tabelaprecoId: this.context.tabelaPreco?.tabelapreco_id,
        condicaopagamentoid: this.context.condicaoPagamento?.condicaopagamentoid,
        tipoPedido: this.context.tipoPedidoCliente?.tipopedidoid
          || this.context.tipopedidoid,
      });
    },
  },
  watch: {
    contextKey: {
      immediate: true,
      handler() {
        this.discover();
      },
    },
  },
  methods: {
    precoOfertaVisual(offer) {
      if (!this.precoEspecialVisual && offer.assessmentFlag !== true) return offer.precoPromocional;
      // PIC base/discount arithmetic is not a fiscal ERP table snapshot.
      return precoVisualizacao({ item: offer, tipoPedido: this.tipoPedidoVisual, assessment: this.item.assessmentFlag === true });
    },
    async discover() {
      const requestId = ++this.requestId;
      this.offers = [];
      this.error = '';
      // A context switch can happen while the prior request is loading.
      // Clear the spinner before every early return.
      this.loading = false;
      if (!this.picAtivo || !this.itemId) return;
      const {
        cliente, estabelecimento, tabelaPreco, condicaoPagamento,
      } = this.context;
      if (!cliente?.clienteid || !estabelecimento?.estabelecimentoid
        || !tabelaPreco?.tabelapreco_id || !condicaoPagamento?.condicaopagamentoid) return;
      this.loading = true;
      try {
        const result = await picService.getPicPedidos({
          clienteid: cliente.clienteid,
          estabelecimentoid: estabelecimento.estabelecimentoid,
          tabelaprecoId: tabelaPreco.tabelapreco_id,
          condicaopagamentoid: condicaoPagamento.condicaopagamentoid,
          itemid: String(this.itemId),
          tipopedidoid: this.context.tipoPedidoCliente?.tipopedidoid
            || this.context.tipopedidoid,
        });
        if (requestId !== this.requestId) return;
        // Never read the shared Vuex cache as a fallback: another SKU can
        // finish a concurrent discovery first. Only this request's response
        // is allowed to populate the inline offer.
        const picState = result?.pic;
        if (!picState) {
          throw new Error('Não foi possível consultar as ofertas PIC para este produto.');
        }
        const allPics = [...(picState.pic_cliente || []), ...(picState.pic_genericas || [])];
        const offers = allPics.flatMap(pic => (pic.ItemsPic || [])
          .filter(item => String(item.itemid) === String(this.itemId)
            || String(item.item_id) === String(this.itemId))
          .map(item => this.toOffer(pic, item)))
          .filter(offer => offer.regraConsumo !== 'CS'
            || Number(offer.picitemquantidadesaldo ?? offer.picitemquantidade) > 0);
        if (requestId !== this.requestId) return;
        this.offers = offers;
      } catch (error) {
        if (requestId === this.requestId) {
          this.error = error.response?.data?.message || 'Não foi possível consultar as ofertas PIC.';
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    toOffer(pic, item) {
      // getPicPedidos already resolves the customer's current table price.
      // picitemprecobase is preferred; item_price is the legacy response
      // field carrying that same authoritative value.
      const base = Number(item.picitemprecobase ?? item.item_price ?? item.precoBase);
      const fixedPrice = Number(item.picitemvalorpic);
      const desconto = Number(item.porcentagem_desconto ?? item.picitemporcentagem);
      const faixas = Array.isArray(item.picitemfaixas) ? item.picitemfaixas : [];
      const descontoExibicao = faixas.length
        ? Number(faixas[0].desconto)
        : desconto;
      const precoPromocional = faixas.length && Number.isFinite(base)
        ? base * (1 - (Number.isFinite(descontoExibicao) ? descontoExibicao : 0) / 100)
        : (Number.isFinite(fixedPrice) && fixedPrice > 0
          ? fixedPrice
          : (Number.isFinite(base) ? base * (1 - (Number.isFinite(desconto) ? desconto : 0) / 100) : 0));
      return {
        ...item,
        picid: pic.picid,
        itemid: item.itemid ?? item.item_id,
        descricao: pic.picdescricao || pic.descricao,
        regraConsumo: pic.RegraConsumo || pic.regraConsumo
          || item.RegraConsumo || item.regra_consumo,
        precoBase: base,
        precoPromocional,
        desconto: Number.isFinite(descontoExibicao) ? descontoExibicao : null,
        faixas,
      };
    },
    formatPrice(value) {
      if (!Number.isFinite(Number(value))) return 'Preço indisponível';
      return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    },
    formatFaixas(faixas) {
      return faixas.map(row => `${row.min}-${row.max || '+'}: ${row.desconto}%`).join(' · ');
    },
  },
};
</script>

<style scoped>
.pic-oferta-inline {
  font-size: 0.8rem;
}
.pic-oferta-inline .text-decoration-line-through {
  text-decoration: line-through;
}
</style>